import { useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Trophy, MagnifyingGlass, Lightning, TrendUp, CheckCircle, ArrowRight, ChartBar, Target, Users, Copy, ShareNetwork, XLogo } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import SEO from "@/components/SEO";
import { trackEvent, API } from "@/App";

const STEPS = [
  { num: "01", icon: MagnifyingGlass, title: "Enter Your Sleeper Username", desc: "No password needed. We pull your leagues, rosters, and settings straight from Sleeper's public API." },
  { num: "02", icon: ChartBar, title: "We Grade Everything", desc: "Every player valued with live dynasty market data. Your roster graded against 9,983 real championship rosters." },
  { num: "03", icon: Trophy, title: "Get Your Championship Plan", desc: "Contender ranking, roster strengths and holes, and specific trade targets that move you closer to a title." },
];

const FEATURES = [
  { icon: Target, color: "#ccff00", title: "Roster Grade & Contender Ranking", desc: "See exactly where your team stands in your league — and whether you should push for a title or start a rebuild." },
  { icon: TrendUp, color: "#4ecdc4", title: "AI Trade Suggestions", desc: "Trade targets built from your actual roster and league context, backed by 167,265 real dynasty trades." },
  { icon: Lightning, color: "#45b7d1", title: "Buy Low / Sell High Alerts", desc: "Live value trends flag which of your players to move now and which league-mates' players to target." },
  { icon: Users, color: "#f9ca24", title: "Championship DNA Comparison", desc: "Compare your roster construction to what actually won in 6,976 completed dynasty leagues." },
];

function TeaserShareRow({ teaser }) {
  const [copied, setCopied] = useState(false);
  const shareText = `My team ranks #${teaser.rank} of ${teaser.total_teams} in total roster value in "${teaser.league_name}". See where yours lands — free Sleeper league analyzer:`;
  const shareUrl = "https://dynastytrademaker.com/sleeper-analyzer";
  const copyMsg = async () => {
    try {
      await navigator.clipboard.writeText(`${shareText} ${shareUrl}`);
      setCopied(true);
      trackEvent("analyzer_teaser_share", "copy");
      setTimeout(() => setCopied(false), 2500);
    } catch { /* clipboard blocked */ }
  };
  const nativeShare = () => {
    trackEvent("analyzer_teaser_share", "native");
    navigator.share({ text: shareText, url: shareUrl }).catch(() => {});
  };
  const shareX = () => {
    trackEvent("analyzer_teaser_share", "x");
    window.open(`https://twitter.com/intent/tweet?text=${encodeURIComponent(shareText)}&url=${encodeURIComponent(shareUrl)}`, "_blank", "noopener,width=550,height=420");
  };
  return (
    <div className="mt-3 pt-3 border-t border-[#262626]" data-testid="analyzer-teaser-share">
      <p className="text-[10px] uppercase tracking-wider text-[#888] mb-2">Challenge your league mates</p>
      <div className="flex items-center gap-2">
        <button onClick={copyMsg} className={`h-9 px-3 flex-1 flex items-center justify-center gap-1.5 rounded-sm border text-[11px] font-bold transition-colors ${copied ? "border-[#4ade80] text-[#4ade80]" : "border-[#333] text-[#ccc] hover:border-[#ccff00] hover:text-[#ccff00]"}`} data-testid="analyzer-share-copy">
          <Copy size={13} weight="bold" /> {copied ? "COPIED — PASTE IN LEAGUE CHAT" : "COPY FOR LEAGUE CHAT"}
        </button>
        {typeof navigator !== "undefined" && navigator.share && (
          <button onClick={nativeShare} className="h-9 px-3 flex items-center justify-center rounded-sm border border-[#333] text-[#ccc] hover:border-[#ccff00] hover:text-[#ccff00] transition-colors" aria-label="Share" data-testid="analyzer-share-native">
            <ShareNetwork size={14} weight="bold" />
          </button>
        )}
        <button onClick={shareX} className="h-9 px-3 flex items-center justify-center rounded-sm border border-[#333] text-[#ccc] hover:border-[#ccff00] hover:text-[#ccff00] transition-colors" aria-label="Share on X" data-testid="analyzer-share-x">
          <XLogo size={14} weight="bold" />
        </button>
      </div>
    </div>
  );
}

function TeaserCard({ teaser, loading, onSignup }) {
  if (loading) return (
    <div className="border border-[#262626] bg-[#131313] rounded-sm p-5 max-w-md mb-4" data-testid="analyzer-teaser-loading">
      <p className="text-xs text-[#ccff00] font-bold uppercase tracking-wider mb-3 animate-pulse">CRUNCHING YOUR ROSTER...</p>
      <div className="space-y-2">{[1, 2, 3].map(i => <div key={i} className="h-8 skeleton rounded-sm" />)}</div>
    </div>
  );
  if (!teaser) return null;
  const shown = teaser.top_players.slice(0, 3);
  const hidden = teaser.top_players.slice(3);
  return (
    <div className="border border-[#262626] bg-[#131313] rounded-sm p-5 max-w-md mb-4" data-testid="analyzer-teaser-card">
      <p className="text-[10px] text-[#888] uppercase tracking-wider mb-1 truncate">{teaser.league_name}{teaser.superflex ? " · SUPERFLEX" : ""}</p>
      <p className="text-sm text-white mb-3">
        Your team ranks <span className="font-mono font-bold text-[#ccff00]">#{teaser.rank} of {teaser.total_teams}</span> in total roster value
        <span className="text-[#888]"> ({teaser.team_value.toLocaleString()} pts)</span>
      </p>
      <div className="space-y-1.5 mb-1.5">
        {shown.map(p => (
          <div key={p.sleeper_id} className="flex items-center gap-2.5 bg-[#0a0a0a] border border-[#1f1f1f] rounded-sm px-2.5 py-1.5">
            <img src={`https://sleepercdn.com/content/nfl/players/thumb/${p.sleeper_id}.jpg`} alt="" className="w-7 h-7 rounded-full object-cover bg-[#262626]" onError={e => { e.target.style.visibility = "hidden"; }} />
            <span className="text-xs font-bold text-white flex-1 truncate">{p.name}</span>
            <span className="text-[10px] text-[#888]">{p.position}</span>
            <span className="font-mono text-xs font-bold text-[#ccff00]">{p.value.toLocaleString()}</span>
          </div>
        ))}
      </div>
      <div className="relative">
        <div className="space-y-1.5 blur-[6px] select-none pointer-events-none" aria-hidden="true">
          {(hidden.length ? hidden : [{ name: "Hidden Player", position: "??", value: 4200 }, { name: "Hidden Player", position: "??", value: 3100 }]).map((p, i) => (
            <div key={i} className="flex items-center gap-2.5 bg-[#0a0a0a] border border-[#1f1f1f] rounded-sm px-2.5 py-1.5">
              <div className="w-7 h-7 rounded-full bg-[#262626]" />
              <span className="text-xs font-bold text-white flex-1">{p.name}</span>
              <span className="text-[10px] text-[#888]">{p.position}</span>
              <span className="font-mono text-xs font-bold text-[#ccff00]">{p.value.toLocaleString()}</span>
            </div>
          ))}
          <div className="h-8 bg-[#0a0a0a] border border-[#1f1f1f] rounded-sm" />
        </div>
        <div className="absolute inset-0 flex items-center justify-center">
          <span className="text-[10px] font-bold uppercase tracking-wider text-white bg-[#0a0a0a]/80 border border-[#333] px-3 py-1.5 rounded-sm">+ {Math.max(teaser.player_count - 3, 0)} more players, grades & trade targets</span>
        </div>
      </div>
      <Button onClick={onSignup} className="w-full h-12 mt-4 bg-[#ccff00] text-black font-bold text-sm rounded-sm hover:bg-[#b8e600] uppercase" data-testid="analyzer-teaser-cta">
        UNLOCK MY FULL ANALYSIS — FREE <ArrowRight size={16} weight="bold" className="ml-2" />
      </Button>
      <p className="text-[11px] text-[#666] mt-2 text-center">Sign in with Google — takes 10 seconds</p>
      <TeaserShareRow teaser={teaser} />
    </div>
  );
}

export default function SleeperAnalyzerPage() {
  const [username, setUsername] = useState("");
  const [checking, setChecking] = useState(false);
  const [found, setFound] = useState(null);
  const [error, setError] = useState("");
  const [teaser, setTeaser] = useState(null);
  const [teaserLoading, setTeaserLoading] = useState(false);

  const handleGoogleSignup = (source) => {
    trackEvent("analyzer_signup_click", source);
    if (found?.username) localStorage.setItem("dtm_prefill_sleeper", found.username);
    window.location.href = "/api/auth/google/login";
  };

  const checkUsername = async (e) => {
    e.preventDefault();
    const u = username.trim();
    if (!u) { setError("Enter your Sleeper username"); return; }
    setChecking(true); setError(""); setFound(null);
    try {
      const res = await axios.get(`https://api.sleeper.app/v1/user/${u}`);
      if (!res.data?.user_id) throw new Error("not found");
      let leagues = [];
      for (const season of ["2026", "2025"]) {
        try {
          const lr = await axios.get(`https://api.sleeper.app/v1/user/${res.data.user_id}/leagues/nfl/${season}`);
          if (Array.isArray(lr.data) && lr.data.length) { leagues = lr.data; break; }
        } catch { /* try next season */ }
      }
      const dynastyCount = leagues.filter(l => l.settings?.type === 2).length;
      setFound({ username: res.data.display_name || u, avatar: res.data.avatar, leagues: leagues.length, dynasty: dynastyCount });
      localStorage.setItem("dtm_prefill_sleeper", u);
      trackEvent("analyzer_username_found", u);
      setTeaserLoading(true);
      axios.get(`${API}/sleeper-analyzer/teaser?username=${encodeURIComponent(u)}`)
        .then(r => { setTeaser(r.data); trackEvent("analyzer_teaser_shown", u); })
        .catch(() => setTeaser(null))
        .finally(() => setTeaserLoading(false));
    } catch {
      setError("Couldn't find that username on Sleeper. Double-check the spelling.");
      trackEvent("analyzer_username_notfound", u);
    }
    setChecking(false);
  };

  return (
    <div className="min-h-screen bg-[#0a0a0a]">
      <SEO title="Free Sleeper League Analyzer — Grade Your Dynasty Team in Seconds" description="Sync your Sleeper league and get an instant roster grade, contender ranking, and championship trade plan. Built on real data from 6,976 dynasty leagues. Free." path="/sleeper-analyzer" image="https://dynastytrademaker.com/ad-graphics/fb-ad-landscape-headline.jpg" />

      {/* Slim header — minimal leak for ad traffic */}
      <header className="border-b border-[#1a1a1a]">
        <div className="max-w-6xl mx-auto px-4 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center no-underline" data-testid="analyzer-logo-link">
            <img src="/brand/logo-wordmark-dark-transparent.png" alt="Dynasty Trade Maker" className="h-9 w-auto" />
          </Link>
          <Button onClick={() => handleGoogleSignup("header")} className="h-9 px-5 bg-[#ccff00] text-black font-bold text-xs rounded-sm hover:bg-[#b8e600] uppercase" data-testid="analyzer-header-cta">
            Analyze Free
          </Button>
        </div>
      </header>

      {/* Hero */}
      <section className="max-w-6xl mx-auto px-4 pt-14 pb-16">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <div>
            <span className="inline-block text-[10px] font-bold uppercase tracking-[0.3em] text-[#ccff00] border border-[#ccff00]/30 px-3 py-1.5 rounded-sm mb-5">FREE SLEEPER LEAGUE ANALYZER</span>
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl tracking-tight text-white uppercase leading-[0.95] mb-5">
              ANALYZE YOUR SLEEPER LEAGUE <span className="text-[#ccff00]">IN SECONDS</span>
            </h1>
            <p className="text-base md:text-lg text-[#a3a3a3] leading-relaxed mb-8 max-w-lg">
              Enter your Sleeper username and get an instant roster grade, contender ranking, and a championship trade plan — powered by real data from 6,976 completed dynasty leagues.
            </p>

            {/* Username hook */}
            {!found ? (
              <form onSubmit={checkUsername} className="mb-4">
                <div className="flex flex-col sm:flex-row gap-3 max-w-md">
                  <Input value={username} onChange={e => setUsername(e.target.value)} placeholder="Your Sleeper username" className="h-12 bg-[#171717] border-[#333] text-white text-sm rounded-sm flex-1" data-testid="analyzer-username-input" />
                  <Button type="submit" disabled={checking} className="h-12 px-6 bg-[#ccff00] text-black font-bold text-sm rounded-sm hover:bg-[#b8e600] uppercase shrink-0" data-testid="analyzer-check-btn">
                    {checking ? "CHECKING..." : "FIND MY LEAGUES"}
                  </Button>
                </div>
                {error && <p className="text-xs text-[#ff6b6b] mt-2" data-testid="analyzer-error">{error}</p>}
                <p className="text-xs text-[#666] mt-3">No password required. No credit card. Free roster analysis.</p>
              </form>
            ) : (
              <div>
                <div className="border border-[#ccff00]/40 bg-[#ccff00]/5 rounded-sm p-4 max-w-md mb-3" data-testid="analyzer-found-card">
                  <div className="flex items-center gap-3">
                    {found.avatar && <img src={`https://sleepercdn.com/avatars/thumbs/${found.avatar}`} alt="" className="w-10 h-10 rounded-full" />}
                    <div className="flex-1">
                      <p className="text-sm font-bold text-white flex items-center gap-1.5"><CheckCircle size={16} weight="fill" className="text-[#ccff00]" /> Found @{found.username}</p>
                      <p className="text-xs text-[#a3a3a3]">{found.leagues} league{found.leagues !== 1 ? "s" : ""} ready to analyze{found.dynasty > 0 ? ` — ${found.dynasty} dynasty` : ""}</p>
                    </div>
                  </div>
                  {!teaserLoading && !teaser && (
                    <Button onClick={() => handleGoogleSignup("found_card")} className="w-full h-12 mt-3 bg-[#ccff00] text-black font-bold text-sm rounded-sm hover:bg-[#b8e600] uppercase" data-testid="analyzer-found-cta">
                      SEE MY FULL ANALYSIS — FREE <ArrowRight size={16} weight="bold" className="ml-2" />
                    </Button>
                  )}
                </div>
                <TeaserCard teaser={teaser} loading={teaserLoading} onSignup={() => handleGoogleSignup("teaser")} />
              </div>
            )}
          </div>

          <div className="hidden lg:block">
            <img src="/ad-graphics/fb-ad-square-headline.jpg" alt="Sleeper league analysis dashboard showing an A- roster grade" className="w-full rounded-sm border border-[#262626]" loading="eager" />
          </div>
        </div>
      </section>

      {/* Data credibility strip */}
      <section className="border-y border-[#1a1a1a] bg-[#0d0d0d]">
        <div className="max-w-6xl mx-auto px-4 py-8 grid grid-cols-2 md:grid-cols-4 gap-6 text-center">
          {[["6,976", "Championship Leagues Analyzed"], ["9,983", "Champion Rosters Studied"], ["167,265", "Real Dynasty Trades"], ["400+", "Players Valued Live"]].map(([v, l]) => (
            <div key={l}>
              <p className="font-mono text-2xl md:text-3xl font-bold text-[#ccff00]">{v}</p>
              <p className="text-[11px] uppercase tracking-wider text-[#888] mt-1">{l}</p>
            </div>
          ))}
        </div>
      </section>

      {/* How it works */}
      <section className="max-w-6xl mx-auto px-4 py-16">
        <h2 className="font-heading text-2xl md:text-3xl text-white uppercase text-center mb-3">FROM USERNAME TO GAME PLAN IN <span className="text-[#ccff00]">3 STEPS</span></h2>
        <p className="text-sm text-[#a3a3a3] text-center mb-10 max-w-xl mx-auto">No spreadsheets. No manual entry. Your entire league synced and analyzed automatically.</p>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
          {STEPS.map(s => {
            const Icon = s.icon;
            return (
              <div key={s.num} className="border border-[#262626] bg-[#131313] p-6 rounded-sm">
                <div className="flex items-center justify-between mb-4">
                  <div className="w-10 h-10 rounded-sm bg-[#ccff00]/10 flex items-center justify-center"><Icon size={20} weight="bold" className="text-[#ccff00]" /></div>
                  <span className="font-mono text-xs text-[#555]">{s.num}</span>
                </div>
                <p className="text-sm font-bold text-white mb-2">{s.title}</p>
                <p className="text-xs text-[#888] leading-relaxed">{s.desc}</p>
              </div>
            );
          })}
        </div>
      </section>

      {/* What you get */}
      <section className="bg-[#0d0d0d] border-y border-[#1a1a1a]">
        <div className="max-w-6xl mx-auto px-4 py-16">
          <h2 className="font-heading text-2xl md:text-3xl text-white uppercase text-center mb-10">EVERYTHING YOU NEED TO <span className="text-[#ccff00]">WIN A CHAMPIONSHIP</span></h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
            {FEATURES.map(f => {
              const Icon = f.icon;
              return (
                <div key={f.title} className="flex items-start gap-4 border border-[#262626] bg-[#131313] p-6 rounded-sm">
                  <div className="w-11 h-11 rounded-sm flex items-center justify-center shrink-0" style={{ backgroundColor: `${f.color}15` }}>
                    <Icon size={22} weight="bold" style={{ color: f.color }} />
                  </div>
                  <div>
                    <p className="text-sm font-bold text-white mb-1.5">{f.title}</p>
                    <p className="text-xs text-[#888] leading-relaxed">{f.desc}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Proof / research angle */}
      <section className="max-w-4xl mx-auto px-4 py-16 text-center">
        <h2 className="font-heading text-2xl md:text-3xl text-white uppercase mb-4">NOT OPINIONS. <span className="text-[#ccff00]">REAL CHAMPIONSHIP DATA.</span></h2>
        <p className="text-sm md:text-base text-[#a3a3a3] leading-relaxed max-w-2xl mx-auto mb-6">
          We crawled 6,976 completed Sleeper dynasty leagues and studied every champion — every roster, every trade, every draft pick. Your analysis is measured against what actually won, not what an expert guessed. 78.4% of champions had a top-10 RB. 71.6% won without a top-3 QB. We know, because we counted.
        </p>
        <Link to="/research" className="text-xs text-[#ccff00] uppercase tracking-wider font-bold inline-flex items-center gap-1.5 no-underline hover:underline" data-testid="analyzer-research-link">Browse the 33 research articles <ArrowRight size={12} weight="bold" /></Link>
      </section>

      {/* Final CTA */}
      <section className="border-t border-[#1a1a1a] bg-[#0d0d0d]">
        <div className="max-w-3xl mx-auto px-4 py-16 text-center">
          <Trophy size={36} weight="fill" className="text-[#ccff00] mx-auto mb-4" />
          <h2 className="font-heading text-3xl md:text-4xl text-white uppercase mb-4">IS YOUR TEAM BUILT TO WIN?</h2>
          <p className="text-sm md:text-base text-[#a3a3a3] mb-8">Find out in the next 60 seconds. Free roster grade, contender ranking, and your first trade suggestions.</p>
          <Button onClick={() => handleGoogleSignup("footer")} className="h-13 px-10 py-4 bg-[#ccff00] text-black font-bold text-base rounded-sm hover:bg-[#b8e600] uppercase" data-testid="analyzer-footer-cta">
            ANALYZE MY SLEEPER LEAGUE — FREE
          </Button>
          <p className="text-[11px] text-[#666] mt-4">Free forever plan available. No credit card required.</p>
        </div>
      </section>
    </div>
  );
}
